import axios from "axios";

import { API_BASE_URL } from "./config";
import { emitAuthTelemetry, createAuthCorrelationId } from "./authTelemetry";
import { runWithBrowserRefreshLock } from "./refreshLock";
import {
    clearAuthState,
    getAccessToken,
    getRefreshToken,
    hasFreshAccessToken,
    readAuthSnapshot,
    toRefreshResponse,
    writeRefreshAuthState,
} from "./authState";
import { redirectToPortalLogin } from "../utils/authRedirect";

const TRUSTED_REFRESH_ERROR_CODES = new Set(["invalid_refresh_token", "token_reuse_detected"]);
const SERVER_REFRESH_UNAVAILABLE_CODE = "refresh_rotation_unavailable";
const MAX_UNKNOWN_REFRESH_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 250;

let pendingRefreshPromise = null;
let sessionTerminationStarted = false;

export class AuthRefreshError extends Error {
    constructor(message, { category, code, response, cause, correlationId, entrypoint } = {}) {
        super(message);
        this.name = "AuthRefreshError";
        this.authCategory = category;
        this.authCode = code;
        this.response = response;
        this.cause = cause;
        this.correlationId = correlationId;
        this.entrypoint = entrypoint;
    }
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function retryDelay(attempt) {
    return BASE_RETRY_DELAY_MS * attempt + Math.floor(Math.random() * 150);
}

function getResponseContentType(response) {
    const headers = response?.headers;
    if (!headers) {
        return "";
    }
    if (typeof headers.get === "function") {
        return headers.get("content-type") || headers.get("Content-Type") || "";
    }
    return headers["content-type"] || headers["Content-Type"] || "";
}

function getStructuredErrorCode(data) {
    const detail = data?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
        return detail.code || null;
    }
    return data?.code || data?.error?.code || null;
}

function isTrustedRefreshEndpointResponse(error) {
    const response = error?.response;
    const requestUrl = String(error?.config?.url || "");
    const contentType = getResponseContentType(response);
    return Boolean(
        response
            && requestUrl.includes("/auth/refresh")
            && contentType.toLowerCase().includes("application/json")
            && response.data
            && typeof response.data === "object",
    );
}

function classifyRefreshError(error, context) {
    if (error instanceof AuthRefreshError) {
        return error;
    }

    if (!error?.response) {
        return new AuthRefreshError("刷新结果未知", {
            category: "unknown_result",
            code: error?.code || "transport_unknown",
            cause: error,
            correlationId: context.correlationId,
            entrypoint: context.entrypoint,
        });
    }

    const response = error.response;
    const status = response.status;
    const code = isTrustedRefreshEndpointResponse(error) ? getStructuredErrorCode(response.data) : null;

    if (status === 401 && TRUSTED_REFRESH_ERROR_CODES.has(code)) {
        return new AuthRefreshError("刷新令牌已失效", {
            category: "deterministic_invalid",
            code,
            response,
            cause: error,
            correlationId: context.correlationId,
            entrypoint: context.entrypoint,
        });
    }

    if (status === 503 && code === SERVER_REFRESH_UNAVAILABLE_CODE) {
        return new AuthRefreshError("刷新服务暂不可用", {
            category: "unknown_result",
            code,
            response,
            cause: error,
            correlationId: context.correlationId,
            entrypoint: context.entrypoint,
        });
    }

    return new AuthRefreshError("刷新结果未知", {
        category: "unknown_result",
        code: code || `http_${status}`,
        response,
        cause: error,
        correlationId: context.correlationId,
        entrypoint: context.entrypoint,
    });
}

function validateRefreshResponse(data, context) {
    if (!data?.access_token || !data?.refresh_token || !data?.expires_at) {
        throw new AuthRefreshError("刷新响应缺少必要字段", {
            category: "unknown_result",
            code: "malformed_refresh_response",
            correlationId: context.correlationId,
            entrypoint: context.entrypoint,
        });
    }
}

function terminateAuthSession(error, context) {
    if (sessionTerminationStarted) {
        return;
    }
    sessionTerminationStarted = true;
    clearAuthState({ correlationId: context.correlationId, entrypoint: context.entrypoint });
    emitAuthTelemetry({
        correlationId: context.correlationId,
        entrypoint: context.entrypoint,
        stage: "terminate",
        outcome: "success",
        category: error.authCategory,
        error,
    });
    if (typeof window !== "undefined") {
        redirectToPortalLogin(window.location.pathname + window.location.search, { replace: true });
    }
}

async function requestRefreshToken(context) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        throw new AuthRefreshError("没有刷新令牌", {
            category: "deterministic_invalid",
            code: "missing_refresh_token",
            correlationId: context.correlationId,
            entrypoint: context.entrypoint,
        });
    }

    emitAuthTelemetry({
        correlationId: context.correlationId,
        entrypoint: context.entrypoint,
        stage: "request",
        outcome: "start",
        detail: { attempt: context.attempt },
    });

    const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refresh_token: refreshToken },
        {
            headers: {
                "Content-Type": "application/json",
                "X-Auth-Refresh-Correlation": context.correlationId,
            },
            timeout: 30000,
        },
    );

    validateRefreshResponse(response.data, context);
    emitAuthTelemetry({
        correlationId: context.correlationId,
        entrypoint: context.entrypoint,
        stage: "commit",
        outcome: "success",
    });

    const commitResult = writeRefreshAuthState(response.data, {
        correlationId: context.correlationId,
        entrypoint: context.entrypoint,
    });

    emitAuthTelemetry({
        correlationId: context.correlationId,
        entrypoint: context.entrypoint,
        stage: "persist",
        outcome: commitResult.persisted ? "success" : "degraded",
        category: commitResult.persisted ? null : "local_degraded",
    });
    emitAuthTelemetry({
        correlationId: context.correlationId,
        entrypoint: context.entrypoint,
        stage: "broadcast",
        outcome: commitResult.broadcasted ? "success" : "degraded",
        category: commitResult.broadcasted ? null : "local_degraded",
    });

    return {
        ...response.data,
        auth_degraded: Boolean(commitResult.degraded),
    };
}

async function refreshWithRetries(context) {
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_UNKNOWN_REFRESH_ATTEMPTS; attempt += 1) {
        const attemptContext = { ...context, attempt };
        try {
            return await requestRefreshToken(attemptContext);
        } catch (error) {
            const classified = classifyRefreshError(error, attemptContext);
            lastError = classified;
            emitAuthTelemetry({
                correlationId: context.correlationId,
                entrypoint: context.entrypoint,
                stage: "request",
                outcome: "failure",
                category: classified.authCategory,
                error: classified,
                detail: { attempt },
            });

            if (classified.authCategory === "deterministic_invalid") {
                throw classified;
            }
            if (attempt < MAX_UNKNOWN_REFRESH_ATTEMPTS) {
                await sleep(retryDelay(attempt));
            }
        }
    }
    throw lastError;
}

async function refreshAuthSessionInternal(options) {
    const context = {
        entrypoint: options.entrypoint || "unknown",
        correlationId: options.correlationId || createAuthCorrelationId(options.entrypoint || "unknown"),
    };
    const beforeSnapshot = options.beforeSnapshot || readAuthSnapshot();

    try {
        const result = await runWithBrowserRefreshLock(
            async () => {
                const reused = toRefreshResponse(readAuthSnapshot());
                if (!options.force && reused?.access_token && hasFreshAccessToken(readAuthSnapshot())) {
                    return reused;
                }
                return refreshWithRetries(context);
            },
            { beforeSnapshot, entrypoint: context.entrypoint, correlationId: context.correlationId },
        );
        sessionTerminationStarted = false;
        return result;
    } catch (error) {
        const classified = classifyRefreshError(error, context);
        if (classified.authCategory === "deterministic_invalid") {
            terminateAuthSession(classified, context);
        }
        throw classified;
    }
}

export function refreshAuthSession(options = {}) {
    if (pendingRefreshPromise) {
        emitAuthTelemetry({
            correlationId: options.correlationId,
            entrypoint: options.entrypoint || "unknown",
            stage: "queue",
            outcome: "wait",
        });
        return pendingRefreshPromise;
    }

    pendingRefreshPromise = refreshAuthSessionInternal(options).finally(() => {
        pendingRefreshPromise = null;
    });
    return pendingRefreshPromise;
}

export async function getFreshAccessToken(options = {}) {
    const snapshot = readAuthSnapshot();
    if (!options.force && hasFreshAccessToken(snapshot)) {
        return getAccessToken();
    }
    const result = await refreshAuthSession({ ...options, beforeSnapshot: snapshot });
    return result.access_token;
}

export function isAuthRefreshDeterministicError(error) {
    return error?.authCategory === "deterministic_invalid";
}

export function isAuthRefreshUnknownResultError(error) {
    return error?.authCategory === "unknown_result";
}

export function isAuthRefreshLocalDegraded(error) {
    return error?.authCategory === "local_degraded";
}
